import React, { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  connectToKlassSocket,
  disconnectFromKlassSocket,
  selectors,
  fetchKlasses,
  fetchSessions,
  fetchHistoricalStep,
  fetchClientDealingScenarios,
} from 'state';
import { LoadingScreen } from 'components';

import MyHeader from './Header';
import LiveHero from './LiveHero';
import ClassRail from './ClassRail';
import SessionsPanel from './SessionsPanel';
import './dashboard.css';

const Dashboard: React.FC = () => {
  const dispatch = useDispatch();
  const token = useSelector(selectors.token) as string;
  const classesById = useSelector(selectors.klasses);
  // klassesList/sessions are Object.values(...) — a fresh array every call.
  // shallowEqual keeps a stable reference when the contents are unchanged, so
  // we don't re-render (and re-filter/re-sort downstream) on unrelated actions.
  const klassesList = useSelector(selectors.klassesList, shallowEqual);
  const sessions = useSelector(selectors.sessions, shallowEqual);
  const klassesWaiting = useSelector(selectors.klassesWaiting);
  const sessionsWaiting = useSelector(selectors.waitingSessions);
  const scenariosWaiting = useSelector(selectors.clientDealingScenariosWaiting);

  const [selectedId, setSelectedId] = useState<number>(0);

  useEffect(() => {
    dispatch(connectToKlassSocket(token));
    dispatch(fetchKlasses({}));
    dispatch(fetchSessions({}));
    dispatch(fetchHistoricalStep({}));
    dispatch(fetchClientDealingScenarios({}));

    return () => {
      dispatch(disconnectFromKlassSocket(token));
    };
  }, [dispatch, token]);

  // Default the selection to the most recent class, and recover if the
  // currently selected class disappears.
  useEffect(() => {
    const ids = Object.keys(classesById).map(Number);
    if (!ids.length) return;
    if (selectedId === 0 || !ids.includes(selectedId)) {
      setSelectedId(Math.max(...ids));
    }
  }, [classesById, selectedId]);

  const hasClasses = useMemo(
    () => Object.keys(classesById).length > 0,
    [classesById],
  );

  // Gate on sessions too: the hero + rail are fed entirely from the session
  // list, so rendering before it lands would flash "Nothing in progress" / "0
  // sessions" on every cold load.
  if (klassesWaiting || sessionsWaiting || scenariosWaiting)
    return <LoadingScreen />;

  return (
    <div className="td-page">
      <MyHeader />
      <div className="td-root">
        <div className="td-stage">
          <LiveHero sessions={sessions} classesById={classesById} />
          {hasClasses ? (
            <div className="td-md">
              <ClassRail
                klasses={klassesList}
                sessions={sessions}
                selectedId={selectedId}
                onSelect={setSelectedId}
              />
              <SessionsPanel klassId={selectedId} />
            </div>
          ) : (
            <div className="td-panel">
              <div className="td-empty">
                <h4>No classes yet</h4>
                <p>
                  Once you have a training class it will appear here with its
                  sessions.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
